import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faNewspaper,
    faEye,
    faClock,
    faMagnifyingGlass,
    faFilter,
    faPodcast,
    faVideo,
    faImage,
    faFilePdf,
    faRotateRight,
    faSpinner,
    faStar,
    faRobot,
    faTrash,
    faLink
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import apiHelper from '../../utils/apiHelper';
import { cleanHtmlForIndexing } from '../../utils/textUtils';

import NewArticleForm from './ArticleForm';
import './NewsManagement.css';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { repositoryService } from '../../services/repositoryService';
import { newsService } from '../../services/newsService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import * as XLSX from 'xlsx';
import { galleryService } from '../../services/galleryService';
import { daireService } from '../../services/daireService';

// Update the article type badge function
const getArticleTypeBadge = (article) => {
    if (article.isWeeklyNews) return 'Weekly';
    if (article.isSubmittedArticle) return 'Submitted';
    return 'News'; // Default type instead of 'Standard'
};

function NewsManagement({ 
    newsArticles,
    weeklySections,
    repositoryItems: initialRepositoryItems = [],
    documentTypes = [],
    workingGroups = [],
    onUploadDocument,
    fetchNews,
    isLoading
}) {
    const { user } = useAuth0();
    const [repositoryItems, setRepositoryItems] = useState(initialRepositoryItems || []);
    const [newsSearchTerm, setNewsSearchTerm] = useState('');
    const [newsFilter, setNewsFilter] = useState('all');
    const [selectedWeeklySection, setSelectedWeeklySection] = useState('all');
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showNewArticleForm, setShowNewArticleForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedType, setSelectedType] = useState('all');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: 'portalPublishDate',
        direction: 'desc'
    });
    const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
    const [isIndexing, setIsIndexing] = useState(false);
    const [isScanningLinks, setIsScanningLinks] = useState(false);
    const [foundLinks, setFoundLinks] = useState([]);
    const [showLinksModal, setShowLinksModal] = useState(false);
    const [showArticleForm, setShowArticleForm] = useState(false);
    const [selectedArticleForEdit, setSelectedArticleForEdit] = useState(null);
    const [linkFilter, setLinkFilter] = useState('all');
    const [articleTypeFilter, setArticleTypeFilter] = useState('all');
    // Add new state for GUID matching
    const [guidMappings, setGuidMappings] = useState([]);
    const [showGuidMatchModal, setShowGuidMatchModal] = useState(false);
    const [matchedResults, setMatchedResults] = useState([]);
    const [isMatching, setIsMatching] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [showNewsReferencedOnly, setShowNewsReferencedOnly] = useState(false);
    // Add gallery state and loading
    const [galleries, setGalleries] = useState([]);
    const [isLoadingGalleries, setIsLoadingGalleries] = useState(false);
    // Add news indexing state
    const [isIndexingNews, setIsIndexingNews] = useState(false);


    const articlesPerPage = 20;  // Add this back

    // Add robust date parsing function
    const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0); // Return epoch for empty dates (sorts to bottom)
        
        try {
            // Handle ISO string format (2025-02-13T00:00:00.000Z)
            if (dateStr.includes('T')) {
                return new Date(dateStr);
            }
            
            // Handle YYYY-MM-DD format
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return new Date(dateStr + 'T00:00:00.000Z');
            }
            
            // Handle DD/MM/YYYY format
            if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                const [day, month, year] = dateStr.split('/');
                return new Date(`${year}-${month}-${day}T00:00:00.000Z`);
            }
            
            // Handle MM/DD/YYYY format
            if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                const [month, day, year] = dateStr.split('/');
                return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
            }
            
            // Handle DD-MM-YYYY format
            if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const [day, month, year] = dateStr.split('-');
                return new Date(`${year}-${month}-${day}T00:00:00.000Z`);
            }
            
            // Try direct parsing as fallback
            const parsed = new Date(dateStr);
            return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            
        } catch (error) {
            console.warn('Date parsing error for:', dateStr, error);
            return new Date(0); // Return epoch for invalid dates
        }
    };

    // Handle column sort
    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key 
                ? current.direction === 'asc' ? 'desc' : 'asc'
                : 'asc'
        }));
    };

    // Clean up filteredNews without logging
    const filteredNews = useMemo(() => {
              
        // First filter the articles and ensure we only have valid articles
        let filtered = [...newsArticles]
            .filter(article => article && typeof article === 'object') // Safety check for undefined articles
            .filter(article => {  
                // Base text search
                const matchesSearch = article.title?.toLowerCase().includes(newsSearchTerm.toLowerCase()) ||
                                    article.content?.toLowerCase().includes(newsSearchTerm.toLowerCase());

                // Type filter
                const matchesType = selectedType === 'all' || 
                                  (selectedType === 'submitted' && article.isSubmittedArticle) ||
                                  (selectedType === 'weekly' && article.isWeeklyNews);

                // Section filter
                const matchesSection = selectedWeeklySection === 'all' || 
                                     article.weeklySection === selectedWeeklySection;

                // Featured filter
                const matchesFeatured = !showFeaturedOnly || article.isFeatured;

                return matchesSearch && matchesType && matchesSection && matchesFeatured;
            });

        // Then apply sorting
        filtered.sort((a, b) => {
            switch (sortConfig.key) {
                case 'date':
                    const dateA = parseDate(a.issueDate);
                    const dateB = parseDate(b.issueDate);
                    return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
                
                case 'portalPublishDate':
                    const portalDateA = parseDate(a.portalPublishDate);
                    const portalDateB = parseDate(b.portalPublishDate);
                    return sortConfig.direction === 'asc' ? portalDateA - portalDateB : portalDateB - portalDateA;
                
                case 'title':
                    const titleA = (a.title || '').toLowerCase();
                    const titleB = (b.title || '').toLowerCase();
                    return sortConfig.direction === 'asc' 
                        ? titleA.localeCompare(titleB)
                        : titleB.localeCompare(titleA);
                
                case 'type':
                    const typeA = getArticleTypeBadge(a);
                    const typeB = getArticleTypeBadge(b);
                    return sortConfig.direction === 'asc'
                        ? typeA.localeCompare(typeB)
                        : typeB.localeCompare(typeA);
                
                case 'status':
                    const statusA = a.published ? 'published' : 'draft';
                    const statusB = b.published ? 'published' : 'draft';
                    return sortConfig.direction === 'asc'
                        ? statusA.localeCompare(statusB)
                        : statusB.localeCompare(statusA);
                
                case 'featured':
                    const featuredA = a.isFeatured ? 1 : 0;
                    const featuredB = b.isFeatured ? 1 : 0;
                    return sortConfig.direction === 'asc' 
                        ? featuredA - featuredB 
                        : featuredB - featuredA;
                
                default:
                    return 0;
            }
        });

      
        return filtered;
    }, [newsArticles, newsSearchTerm, selectedType, selectedWeeklySection, sortConfig, showFeaturedOnly]);

    // Calculate pagination
    const indexOfLastArticle = currentPage * articlesPerPage;
    const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
    const currentArticles = filteredNews.slice(indexOfFirstArticle, indexOfLastArticle);
    const totalPages = Math.ceil(filteredNews.length / articlesPerPage);

    // Update the displayDate function with more logging
    const displayDate = (dateStr) => {
        if (!dateStr) return '';
        
        try {
            // Handle ISO string format (2025-02-13T00:00:00.000Z)
            if (dateStr.includes('T')) {
                const date = new Date(dateStr);
                return date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
            
            // If it's already in YYYY-MM-DD format
            if (dateStr.includes('-')) {
                const [year, month, day] = dateStr.split('-');
                return `${day}/${month}/${year}`;
            }
            
            // If it's already in DD/MM/YYYY format, return as is
            return dateStr;
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateStr;
        }
    };

    // Update handleArticleClick with data mapping
    const handleArticleClick = (article) => {
        const mappedArticle = {
            id: article.id,
            title: article.title || '',
            content: article.content || '',
            summary: article.summary || '',
            issueDate: article.issueDate || '',
            portalPublishDate: article.portalPublishDate || '',
            status: article.published ? 'Published' : 'Draft',
            // Media fields
            imageName: article.image || '',
            videoName: article.video || '',
            podcastName: article.audio || '',
            pdfName: article.document || article.pdf || '', // Added document as fallback
            briefingDate: article.briefingDate || article.BriefingDate || '',
            // Type flags
            isWeeklyNews: Boolean(article.isWeeklyNews),
            isSubmittedArticle: Boolean(article.isSubmittedArticle),
            isFeatured: Boolean(article.isFeatured),
            // Featured dates - ensure proper date format
            featuredStartDate: article.featuredStartDate || '',
            featuredEndDate: article.featuredEndDate || '',
            featuredIndefinitely: Boolean(article.featuredIndefinitely),
            // Categories
            weeklySection: article.weeklySection || '',
            strategicCategory: article.strategicCategory || '',
            // Author fields - match SharePoint field names
            author: article.Author0 || article.author || '',
            submittedAuthor: article.SubmittedAuthor || article.author || '',
            submittedCompany: article.Company || article.submittedCompany || '',
            // Ensure we have the created by field
            createdByStaff: article.createdByStaff || article.CreatedByStaff || '',
            // Add social media fields
            SocialMediaPostLinkedIn: article.socialMediaPostLinkedIn || '',
            SocialMediaPostTwitterX: article.socialMediaPostTwitterX || '',
            SocialMediaPostFacebook: article.socialMediaPostFacebook || ''
        };

        setSelectedArticle(mappedArticle);
        setShowNewArticleForm(true);
    };

    // Update the handleNewArticle function
    const handleNewArticle = async () => {
        // Ensure galleries are loaded before showing the form
        if (galleries.length === 0) {
            await loadGalleries();
        }
        
       
        setShowNewArticleForm(true);
    };

    const handleNewArticleClose = () => {
        setShowNewArticleForm(false);
        setSelectedArticle(null);
    };

    // Update handleNewArticleSave to check existence before saving
    const handleNewArticleSave = async (articleData) => {
        try {
            let articleId;
            
            if (articleData.id) {
                // Editing existing article
                await newsService.updateArticle(articleData.id, articleData, user?.name);
                articleId = articleData.id;
            } else {
                // Creating new article
                const response = await newsService.createArticle(articleData, user?.name);
                
                // Extract ID from response - may be in response.data.id or response.id
                articleId = response?.data?.id || response?.id || response?.data?.d?.Id;
            }
            
            // Refresh the news list
            await fetchNews();
            
            // Close the form
            setShowNewArticleForm(false);
            setSelectedArticle(null);
            
            // Return the article ID for potential auto-indexing
            return { success: true, articleId };
        } catch (error) {
            console.error('Error saving article:', error);
            throw error; 
        }
    };

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [newsSearchTerm, newsFilter, selectedWeeklySection, selectedType]);


    // Wrap the fetchNews function to handle loading state
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchNews();
        } finally {
            setIsRefreshing(false);
        }
    };

    // Update SortIndicator to only show for sortable columns
    const SortIndicator = ({ column }) => {
        const sortableColumns = ['date', 'portalPublishDate', 'title', 'type', 'status'];
        if (!sortableColumns.includes(column) || sortConfig.key !== column) return null;
        
        return (
            <span className="sort-indicator">
                {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
            </span>
        );
    };

    // Add a function to generate unique keys
    const getUniqueKey = (article) => {
        // Combine multiple properties to ensure uniqueness
        return `${article.id}-${article.title}-${article.issueDate}`.replace(/\s+/g, '-');
    };

    // Update getMediaIcons to use this debug info
    const getMediaIcons = (article) => {
 
        return (
            <div className="media-icons">
                {article.image && (
                    <FontAwesomeIcon 
                        icon={faImage} 
                        className="media-icon image"
                        title="Image" 
                    />
                )}
                {article.video && (
                    <FontAwesomeIcon 
                        icon={faVideo} 
                        className="media-icon video"
                        title="Video" 
                    />
                )}
                {article.audio && (
                    <FontAwesomeIcon 
                        icon={faPodcast} 
                        className="media-icon podcast"
                        title="Audio" 
                    />
                )}
                {article.pdf && (
                    <FontAwesomeIcon 
                        icon={faFilePdf} 
                        className="media-icon pdf"
                        title="PDF" 
                    />
                )}
            </div>
        );
    };

    // Add these functions
    const fetchDocuments = async () => {
        return []; // Empty array until we actually need to load documents
    };

    // Add gallery loading function
    const loadGalleries = async () => {
       
        setIsLoadingGalleries(true);
        try {
            const galleriesData = await galleryService.getGalleries();
           
            setGalleries(galleriesData || []);
           
        } catch (error) {
            console.error('Error loading galleries:', error);
            toast.error('Failed to load galleries');
        } finally {
            setIsLoadingGalleries(false);
        }
    };

    // Add gallery refresh function
    const refreshGalleries = async () => {
       
        await loadGalleries();
    };

    // Handler for indexing news articles only
    const handleIndexNews = async () => {
        if (!window.confirm('This will index all published news articles into Qdrant. Continue?')) {
            return;
        }

        setIsIndexingNews(true);
        const progressToastId = toast.loading('Indexing news articles...', { autoClose: false });

        try {
            const response = await apiHelper.indexNewsArticles();
            
            toast.dismiss(progressToastId);
            
            if (response.success) {
                toast.success(
                    `✅ News Articles Indexed!\n` +
                    `Total: ${response.total}\n` +
                    `Successfully indexed: ${response.indexed}\n` +
                    `Errors: ${response.errors}\n` +
                    `Duration: ${(response.duration / 1000).toFixed(1)}s`,
                    { autoClose: 5000 }
                );
            } else {
                toast.error(`Failed to index news: ${response.error || 'Unknown error'}`);
            }
        } catch (error) {
            toast.dismiss(progressToastId);
            console.error('Error indexing news:', error);
            toast.error(`Error: ${error.message || 'Failed to index news articles'}`);
        } finally {
            setIsIndexingNews(false);
        }
    };

    // Load galleries on component mount
    useEffect(() => {
        
        loadGalleries();
    }, []);

 
    //  delete article handler
    const handleDeleteArticle = async (article, event) => {
        // Prevent the row click event from firing
        event.stopPropagation();
        
        const confirmMessage = `Are you sure you want to delete "${article.title}"?\n\nThis action cannot be undone.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            
            
            // Call the delete API with the article type
            await newsService.deleteArticle(article.id, article.isSubmittedArticle);
            
            // Show success message
            toast.success('Article deleted successfully');
            
            // Refresh the news list to update the UI
            await fetchNews();
            
        } catch (error) {
            console.error('Error deleting article:', error);
            toast.error('Failed to delete article: ' + (error.message || 'Unknown error'));
        }
    };

    // function to handle article click
    const handleArticleEdit = (articleId, isSubmittedArticle = null) => {
        // If we have the article type info, use it to find the correct article
        let article;
        if (isSubmittedArticle !== null) {
            article = newsArticles.find(a => 
                a.id === articleId && 
                Boolean(a.isSubmittedArticle) === isSubmittedArticle
            );
        } else {
            // Fallback to old behavior if no type specified
            article = newsArticles.find(a => a.id === articleId);
        }
        
        if (article) {
            setSelectedArticleForEdit(article);
            setShowArticleForm(true);
        } else {
            toast.error(`Article ${articleId} (${isSubmittedArticle ? 'Submitted' : 'Weekly'}) not found`);
        }
    };

    //  function to handle article form close
    const handleArticleFormClose = () => {
        setShowArticleForm(false);
        setSelectedArticleForEdit(null);
    };

    //  function to handle article form save
    const handleArticleFormSave = async (articleData) => {
        try {
            await handleNewArticleSave(articleData);
            setShowArticleForm(false);
            setSelectedArticleForEdit(null);
        } catch (error) {
            console.error('Error saving article:', error);
            toast.error('Failed to save article');
        }
    };

    //  helper functions
    const isEmail = (url) => {
        return url.toLowerCase().startsWith('mailto:') || 
               /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(url);
    };

    const isMemberPortalLink = (url) => {
        return url.toLowerCase().includes('memberportal.irishfunds.ie');
    };

    //  utility functions for GUID matching
    const extractGuidFromUrl = (url) => {
        // Extract GUID from URLs like: https://memberportal.irishfunds.ie/file/?id=c382c7ba-4c2b-4018-a7a0-5036e2d226a0
        const guidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
        const match = url.match(guidPattern);
        return match ? match[0] : null;
    };

    //  function to extract slug from non-GUID member portal URLs
    const extractSlugFromUrl = (url) => {
        // Extract slug from URLs like: https://memberportal.irishfunds.ie/files/detail/macro-prudential-project-group-meeting-record-july-2023
        try {
            const patterns = [
                /memberportal\.irishfunds\.ie\/files\/detail\/([^/?#]+)/i,
                /memberportal\.irishfunds\.ie\/documents\/([^/?#]+)/i,
                /memberportal\.irishfunds\.ie\/file\/([^/?#]+)/i
            ];
            
            for (const pattern of patterns) {
                const match = url.match(pattern);
                if (match && match[1]) {
                    // Convert slug to readable format
                    return match[1]
                        .replace(/-/g, ' ')  // Replace hyphens with spaces
                        .replace(/_/g, ' ')  // Replace underscores with spaces
                        .toLowerCase()
                        .trim();
                }
            }
            return null;
        } catch (error) {
            console.warn('Error extracting slug from URL:', url, error);
            return null;
        }
    };

    //  function to match slug-based URLs directly with repository items
    const findBestMatchForSlug = (slug, repositoryItems, originalUrl) => {
        if (!slug) return null;
        
        let bestMatch = null;
        let bestScore = 0;
        
        // Create search variations of the slug
        const slugVariations = [
            slug,
            slug.replace(/\b(meeting|record|document|report|survey|group|project)\b/g, '').trim(), // Remove common words
            slug.split(' ').filter(word => word.length > 3).join(' '), // Keep only longer words
            slug.replace(/\b\d{4}\b/g, '').trim(), // Remove years
            slug.replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)\b/gi, '').trim() // Remove months
        ].filter(variation => variation.length > 3); // Only keep meaningful variations
        
        for (const repoItem of repositoryItems) {
            const repoSearchTerms = [
                repoItem.title?.toLowerCase(),
                repoItem.fileName?.toLowerCase(),
                repoItem.fileName?.replace(/\.[^/.]+$/, '')?.toLowerCase(), // filename without extension
                repoItem.description?.toLowerCase()
            ].filter(Boolean);
            
            let maxScore = 0;
            
            // Test each slug variation against each repository term
            for (const slugVar of slugVariations) {
                for (const repoTerm of repoSearchTerms) {
                    const score = calculateSimilarity(slugVar, repoTerm);
                    maxScore = Math.max(maxScore, score);
                    
                    // Also test if repo term contains significant parts of the slug
                    const slugWords = slugVar.split(' ').filter(word => word.length > 3);
                    const containmentScore = slugWords.filter(word => 
                        repoTerm.includes(word)
                    ).length / Math.max(slugWords.length, 1);
                    
                    maxScore = Math.max(maxScore, containmentScore * 0.8); // Weight containment slightly lower
                }
            }
            
            if (maxScore > bestScore && maxScore > 0.25) { // Lower threshold for slug matching
                bestScore = maxScore;
                bestMatch = {
                    ...repoItem,
                    matchScore: maxScore,
                    matchedOn: bestScore > 0.7 ? 'Strong' : 'Weak',
                    matchType: 'slug',
                    originalSlug: slug,
                    originalUrl: originalUrl
                };
            }
        }
        
        return bestMatch;
    };

    const parseExcelFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    // Assuming the Excel has columns: Title, GUID, Filename
                    // Skip header row and parse data
                    const mappings = jsonData.slice(1).map((row, index) => ({
                        id: index,
                        title: row[0] || '',
                        guid: row[1] || '',
                        filename: row[2] || '',
                        originalRow: row
                    })).filter(item => item.guid && item.guid.trim() !== '');
                    
                    resolve(mappings);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    // Fuzzy string matching function
    const calculateSimilarity = (str1, str2) => {
        if (!str1 || !str2) return 0;
        
        const s1 = str1.toLowerCase().trim();
        const s2 = str2.toLowerCase().trim();
        
        if (s1 === s2) return 1;
        
        // Simple fuzzy matching using Levenshtein distance
        const matrix = [];
        const len1 = s1.length;
        const len2 = s2.length;
        
        if (len1 === 0) return len2 === 0 ? 1 : 0;
        if (len2 === 0) return 0;
        
        for (let i = 0; i <= len1; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= len2; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= len1; i++) {
            for (let j = 1; j <= len2; j++) {
                if (s1.charAt(i - 1) === s2.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        const maxLength = Math.max(len1, len2);
        return (maxLength - matrix[len1][len2]) / maxLength;
    };

    // Match repository items with GUID mappings
    const findBestMatch = (guidMapping, repositoryItems) => {
        let bestMatch = null;
        let bestScore = 0;
        
        const searchTerms = [
            guidMapping.title,
            guidMapping.filename,
            guidMapping.filename?.replace(/\.[^/.]+$/, '') // filename without extension
        ].filter(Boolean);
        
        for (const repoItem of repositoryItems) {
            const repoSearchTerms = [
                repoItem.title,
                repoItem.fileName,
                repoItem.fileName?.replace(/\.[^/.]+$/, '') // filename without extension
            ].filter(Boolean);
            
            let maxScore = 0;
            
            // Compare all combinations
            for (const searchTerm of searchTerms) {
                for (const repoTerm of repoSearchTerms) {
                    const score = calculateSimilarity(searchTerm, repoTerm);
                    maxScore = Math.max(maxScore, score);
                }
            }
            
            if (maxScore > bestScore && maxScore > 0.3) { // Minimum threshold
                bestScore = maxScore;
                bestMatch = {
                    ...repoItem,
                    matchScore: maxScore,
                    matchedOn: bestScore > 0.8 ? 'Strong' : 'Weak'
                };
            }
        }
        
        return bestMatch;
    };

    //  function to get filtered links
    const getFilteredLinks = () => {
        let filtered = foundLinks;
        
        // Apply URL type filter
        switch (linkFilter) {
            case 'emails':
                filtered = filtered.filter(link => isEmail(link.url));
                break;
            case 'memberportal':
                filtered = filtered.filter(link => isMemberPortalLink(link.url));
                break;
            // Remove the article type cases from here since they're now separate
        }
        
        // Apply article type filter
        switch (articleTypeFilter) {
            case 'submitted':
                filtered = filtered.filter(link => link.articleType === 'Submitted');
                break;
            case 'weekly':
                filtered = filtered.filter(link => link.articleType === 'Weekly');
                break;
            case 'news':
                filtered = filtered.filter(link => link.articleType === 'News');
                break;
            // 'all' case - no additional filtering
        }
        
        return filtered;
    };

    //  function to get counts
    const getLinkCounts = () => {
        const emailCount = foundLinks.filter(link => isEmail(link.url)).length;
        const memberPortalCount = foundLinks.filter(link => isMemberPortalLink(link.url)).length;
        const submittedCount = foundLinks.filter(link => link.articleType === 'Submitted').length;
        const weeklyCount = foundLinks.filter(link => link.articleType === 'Weekly').length;
        const newsCount = foundLinks.filter(link => link.articleType === 'News').length;
        return { emailCount, memberPortalCount, submittedCount, weeklyCount, newsCount };
    };

    //  function to handle Excel export
    const handleExportToExcel = () => {
        const filteredLinks = getFilteredLinks();
        const { emailCount, memberPortalCount, submittedCount, weeklyCount, newsCount } = getLinkCounts();
        
        // Create CSV content
        const headers = ['Date', 'Article ID', 'Article Type', 'Article Title', 'URL'];
        const rows = filteredLinks.map(link => [
            displayDate(link.issueDate),
            link.articleId,
            link.articleType,
            link.articleTitle,
            link.url
        ]);

        // Add summary information
        const summary = [
            ['Link Scan Summary'],
            ['Total Links Found', foundLinks.length],
            ['Email Links', emailCount],
            ['Member Portal Links', memberPortalCount],
            ['Weekly News Links', weeklyCount],
            ['Submitted Article Links', submittedCount],
            ['Other News Links', newsCount],
            ['Current URL Filter', linkFilter],
            ['Current Article Filter', articleTypeFilter],
            ['Filtered Results Count', filteredLinks.length],
            [], // Empty row for spacing
            headers,
            ...rows
        ];

        // Convert to CSV
        const csvContent = summary.map(row => row.map(cell => 
            // Escape quotes and wrap in quotes if contains comma or quote
            typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
                ? `"${cell.replace(/"/g, '""')}"` 
                : cell
        ).join(',')).join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `link_scan_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    //  update the LinksModal component
    const LinksModal = () => {
        if (!showLinksModal) return null;

        const { emailCount, memberPortalCount, submittedCount, weeklyCount, newsCount } = getLinkCounts();
        const filteredLinks = getFilteredLinks();

        return (
            <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div className="modal-content" style={{
                    backgroundColor: 'white',
                    padding: '20px',    
                    borderRadius: '8px',
                    maxWidth: '80%',
                    maxHeight: '80vh',
                    overflow: 'auto'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h2>Found Hyperlinks ({foundLinks.length})</h2>
                            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                                <span style={{ marginRight: '20px' }}>📧 Emails: {emailCount}</span>
                                <span style={{ marginRight: '20px' }}>🔗 Member Portal: {memberPortalCount}</span>
                                <span style={{ marginRight: '20px' }}>📰 Weekly: {weeklyCount}</span>
                                <span style={{ marginRight: '20px' }}>📄 Submitted: {submittedCount}</span>
                                <span>📑 News: {newsCount}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                onClick={handleExportToExcel}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #28a745',
                                    backgroundColor: 'white',
                                    color: '#28a745',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                📊 Export to Excel
                            </button>
                            <button 
                                onClick={() => setShowGuidMatchModal(true)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #17a2b8',
                                    backgroundColor: 'white',
                                    color: '#17a2b8',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon icon={faRobot} /> GUID Match
                            </button>
                            <button 
                                onClick={handleProcessSlugMatching}
                                disabled={isMatching}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #28a745',
                                    backgroundColor: 'white',
                                    color: '#28a745',
                                    cursor: isMatching ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon icon={isMatching ? faSpinner : faLink} spin={isMatching} /> 
                                {isMatching ? 'Matching...' : 'Slug Match'}
                            </button>
                            <button 
                                onClick={() => {
                                    // Get counts for the confirmation message
                                    const memberPortalLinks = foundLinks.filter(link => isMemberPortalLink(link.url));
                                    const guidLinks = memberPortalLinks.filter(link => extractGuidFromUrl(link.url));
                                    const slugLinks = memberPortalLinks.filter(link => extractSlugFromUrl(link.url) && !extractGuidFromUrl(link.url));
                                    
                                    if (window.confirm(
                                        `This will process all member portal links:\n` +
                                        `🔑 GUID-based links: ${guidLinks.length}\n` +
                                        `🏷️ Slug-based links: ${slugLinks.length}\n\n` +
                                        `Note: GUID matching requires Excel file, slug matching works directly.\n` +
                                        `Continue?`
                                    )) {
                                        // If Excel file is available, process GUID matching first, then slugs
                                        // If no Excel file, just process slugs
                                        if (excelFile && guidLinks.length > 0) {
                                            handleProcessGuidMatching();
                                        } else if (slugLinks.length > 0) {
                                            handleProcessSlugMatching();
                                        } else {
                                            toast.info('No processable member portal links found');
                                        }
                                    }
                                }}
                                disabled={isMatching}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #6f42c1',
                                    backgroundColor: 'white',
                                    color: '#6f42c1',
                                    cursor: isMatching ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon icon={isMatching ? faSpinner : faRobot} spin={isMatching} /> 
                                {isMatching ? 'Processing...' : 'Smart Match'}
                            </button>
                            <button 
                                onClick={() => setShowLinksModal(false)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', fontWeight: 'bold' }}>
                                URL Type:
                            </label>
                            <select 
                                value={linkFilter}
                                onChange={(e) => setLinkFilter(e.target.value)}
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    width: '200px'
                                }}
                            >
                                <option value="all">All URL Types</option>
                                <option value="emails">📧 Emails ({emailCount})</option>
                                <option value="memberportal">🔗 Member Portal ({memberPortalCount})</option>
                            </select>
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', fontWeight: 'bold' }}>
                                Article Type:
                            </label>
                            <select 
                                value={articleTypeFilter}
                                onChange={(e) => setArticleTypeFilter(e.target.value)}
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    width: '200px'
                                }}
                            >
                                <option value="all">All Article Types</option>
                                <option value="weekly">📰 Weekly ({weeklyCount})</option>
                                <option value="submitted">📄 Submitted ({submittedCount})</option>
                                <option value="news">📑 News ({newsCount})</option>
                            </select>
                        </div>
                        
                        <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
                            Showing: <strong>{filteredLinks.length}</strong> of {foundLinks.length} links
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '8%' }}>ID</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '8%' }}>Type</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '10%' }}>Date</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '37%' }}>Article</th>
                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '37%' }}>URL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLinks.map((link, index) => (
                                <tr key={index}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{link.articleId}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                                        <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            fontSize: '0.8em',
                                            backgroundColor: link.articleType === 'Submitted' ? '#e3f2fd' : 
                                                           link.articleType === 'Weekly' ? '#f3e5f5' : '#e8f5e8',
                                            color: link.articleType === 'Submitted' ? '#1976d2' : 
                                                   link.articleType === 'Weekly' ? '#7b1fa2' : '#388e3c'
                                        }}>
                                            {link.articleType}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{displayDate(link.issueDate)}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                                        <button
                                            onClick={() => handleArticleEdit(link.articleId, link.isSubmittedArticle)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#007bff',
                                                cursor: 'pointer',
                                                padding: 0,
                                                textAlign: 'left',
                                                textDecoration: 'underline',
                                                width: '100%',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word'
                                            }}
                                            title={`Edit ${link.articleType} article: ${link.articleTitle}`}
                                        >
                                            {link.articleTitle}
                                        </button>
                                    </td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                                        <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', wordBreak: 'break-all' }}>
                                            {link.url}
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Add new function to handle slug-based matching (no Excel file needed)
    const handleProcessSlugMatching = async () => {
        setIsMatching(true);
        const progressToastId = toast.loading('Processing slug-based matching...', { autoClose: false });

        try {
            // Get repository items
            toast.update(progressToastId, { render: 'Loading repository items...' });
            const repoItems = await repositoryService.getAllItems();
           
            // Extract slug-based member portal links (no GUIDs)
            toast.update(progressToastId, { render: 'Processing slug-based member portal links...' });
            const memberPortalLinks = foundLinks.filter(link => isMemberPortalLink(link.url));
            const linksWithSlugs = memberPortalLinks.map(link => ({
                ...link,
                slug: extractSlugFromUrl(link.url),
                uniqueArticleId: `${link.articleId}_${link.isSubmittedArticle ? 'SUBMITTED' : 'WEEKLY'}`
            })).filter(link => link.slug && !extractGuidFromUrl(link.url)); // Only slug-based links, no GUIDs

           

            // Match process for slug-based links
            toast.update(progressToastId, { render: 'Matching slug-based links...' });
            const results = [];

            for (const link of linksWithSlugs) {
                const repositoryMatch = findBestMatchForSlug(link.slug, repoItems, link.url);

                results.push({
                    id: `slug_${link.articleId}_${link.isSubmittedArticle ? 'SUBMITTED' : 'WEEKLY'}`,
                    slugMapping: {
                        title: link.articleTitle,
                        slug: link.slug,
                        url: link.url
                    },
                    newsLink: link,
                    repositoryMatch: repositoryMatch || null,
                    status: repositoryMatch ? 
                        (repositoryMatch.matchScore > 0.7 ? 'Strong Match' : 'Weak Match') : 
                        'No Match',
                    matchScore: repositoryMatch?.matchScore || 0,
                    matchType: 'slug',
                    workflow: `News Article → Slug "${link.slug}" → Repository Match`
                });
            }

            // Sort by match quality
            results.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

            setMatchedResults(results);
            setShowGuidMatchModal(true);

            toast.dismiss(progressToastId);
            
            const strongMatches = results.filter(r => r.status === 'Strong Match').length;
            const weakMatches = results.filter(r => r.status === 'Weak Match').length;
            const noMatches = results.filter(r => r.status === 'No Match').length;

            toast.success(
                `Slug Matching Complete!\n` +
                `🟢 Strong matches: ${strongMatches}\n` +
                `🟡 Weak matches: ${weakMatches}\n` +
                `🔴 No matches: ${noMatches}`
            );

        } catch (error) {
            console.error('Error in slug matching:', error);
            toast.dismiss(progressToastId);
            toast.error('Failed to process slug matching: ' + error.message);
        } finally {
            setIsMatching(false);
        }
    };

    // Add main GUID matching functions
    const handleProcessGuidMatching = async () => {
        if (!excelFile) {
            toast.error('Please select an Excel file first');
            return;
        }

        setIsMatching(true);
        const progressToastId = toast.loading('Processing GUID matching...', { autoClose: false });

        try {
            // Parse Excel file
            toast.update(progressToastId, { render: 'Parsing Excel file...' });
            const mappings = await parseExcelFile(excelFile);
           
            // Get repository items
            toast.update(progressToastId, { render: 'Loading repository items...' });
            const repoItems = await repositoryService.getAllItems();


            // Extract GUIDs from member portal links
            toast.update(progressToastId, { render: 'Extracting GUIDs from news links...' });
            const memberPortalLinks = foundLinks.filter(link => isMemberPortalLink(link.url));
            const linksWithGuids = memberPortalLinks.map(link => ({
                ...link,
                guid: extractGuidFromUrl(link.url),
                // Create unique identifier combining ID and type
                uniqueArticleId: `${link.articleId}_${link.isSubmittedArticle ? 'SUBMITTED' : 'WEEKLY'}`
            })).filter(link => link.guid);

            // Match process
            toast.update(progressToastId, { render: 'Matching files...' });
            const results = [];

            for (const guidMapping of mappings) {
                // Find corresponding link from news articles
                const correspondingLink = linksWithGuids.find(link => 
                    link.guid.toLowerCase() === guidMapping.guid.toLowerCase()
                );

                // Find best repository match using the Excel filename
                // The GUID is just the bridge - we match Excel filename to SharePoint repository
                const repositoryMatch = findBestMatch(guidMapping, repoItems);

                results.push({
                    id: guidMapping.id,
                    guidMapping,
                    newsLink: correspondingLink || null,
                    repositoryMatch: repositoryMatch || null,
                    status: repositoryMatch ? 
                        (repositoryMatch.matchScore > 0.8 ? 'Strong Match' : 'Weak Match') : 
                        'No Match',
                    matchScore: repositoryMatch?.matchScore || 0,
                    // Add workflow explanation
                    workflow: correspondingLink ? 
                        `News Article → GUID ${guidMapping.guid} → Excel Filename "${guidMapping.filename}" → Repository Match` :
                        `Excel Filename "${guidMapping.filename}" → Repository Match (No News Reference)`
                });
            }

            // Sort by match quality
            results.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

            setMatchedResults(results);
            setGuidMappings(mappings);
            setShowGuidMatchModal(true);

            toast.dismiss(progressToastId);
            
            const strongMatches = results.filter(r => r.status === 'Strong Match').length;
            const weakMatches = results.filter(r => r.status === 'Weak Match').length;
            const noMatches = results.filter(r => r.status === 'No Match').length;

            toast.success(
                `GUID Matching Complete!\n` +
                `🟢 Strong matches: ${strongMatches}\n` +
                `🟡 Weak matches: ${weakMatches}\n` +
                `🔴 No matches: ${noMatches}`
            );

        } catch (error) {
            console.error('Error in GUID matching:', error);
            toast.dismiss(progressToastId);
            toast.error('Failed to process GUID matching: ' + error.message);
        } finally {
            setIsMatching(false);
        }
    };



    const handleExcelFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setExcelFile(file);
            toast.success(`Excel file "${file.name}" loaded`);
        }
    };

    const handleExportMatchResults = () => {
        if (matchedResults.length === 0) {
            toast.error('No match results to export');
            return;
        }

        // Filter results based on current view
        const resultsToExport = showNewsReferencedOnly 
            ? matchedResults.filter(result => result.newsLink)
            : matchedResults;

        if (resultsToExport.length === 0) {
            toast.error('No results match current filter');
            return;
        }

        // Create comprehensive export data
        const exportData = resultsToExport.map((result, index) => ([
            // Source Mapping Data (GUID or Slug)
            result.guidMapping?.title || result.slugMapping?.title || 'Unknown',
            result.guidMapping?.guid || result.slugMapping?.slug || 'N/A',
            result.guidMapping?.filename || 'N/A',
            result.matchType || 'guid',
            
            // News Link Data
            result.newsLink ? result.newsLink.articleTitle : 'Not Found in News',
            result.newsLink ? result.newsLink.articleId : '',
            result.newsLink ? result.newsLink.articleType : '',
            result.newsLink ? result.newsLink.issueDate : '',
            result.newsLink ? result.newsLink.url : '',
            
            // Repository Match Data
            result.repositoryMatch ? result.repositoryMatch.title : 'No Match',
            result.repositoryMatch ? result.repositoryMatch.fileName : '',
            result.repositoryMatch ? result.repositoryMatch.id : '',
            result.repositoryMatch ? result.repositoryMatch.url : '',
            result.repositoryMatch ? result.repositoryMatch.type : '',
            result.repositoryMatch ? result.repositoryMatch.workingGroup : '',
            
            // Match Quality
            result.status,
            result.matchScore ? (result.matchScore * 100).toFixed(1) + '%' : '0%',
            result.workflow || 'N/A'
        ]));

        // Add headers
        const headers = [
            'Source Title', 'GUID/Slug', 'Excel Filename', 'Match Type',
            'News Article', 'Article ID', 'Article Type', 'Article Date', 'Member Portal URL',
            'Repository Title', 'Repository Filename', 'Repository ID', 'Repository URL', 'Document Type', 'Working Group',
            'Match Status', 'Match Score', 'Workflow'
        ];

        // Add summary
        const summary = [
            ['GUID Matching Results Summary'],
            ['Generated:', new Date().toISOString()],
            ['Filter Applied:', showNewsReferencedOnly ? 'News Referenced Only' : 'All Results'],
            ['Total Records in Export:', resultsToExport.length],
            ['Total Excel Records:', matchedResults.length],
            ['Strong Matches:', resultsToExport.filter(r => r.status === 'Strong Match').length],
            ['Weak Matches:', resultsToExport.filter(r => r.status === 'Weak Match').length],
            ['No Matches:', resultsToExport.filter(r => r.status === 'No Match').length],
            ['Referenced in News:', resultsToExport.filter(r => r.newsLink).length],
            [], // Empty row for spacing
            headers,
            ...exportData
        ];

        // Convert to CSV
        const csvContent = summary.map(row => row.map(cell => 
            typeof cell === 'string' && (cell.includes(',') || cell.includes('"')) 
                ? `"${cell.replace(/"/g, '""')}"` 
                : cell
        ).join(',')).join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const filename = showNewsReferencedOnly 
            ? `guid_matching_news_only_${new Date().toISOString().split('T')[0]}.csv`
            : `guid_matching_results_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Match results exported to CSV');
    };

    // Add the GUID Match Modal component
    const GuidMatchModal = () => {
        if (!showGuidMatchModal) return null;

        return (
            <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div className="modal-content" style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    maxWidth: '95%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    minWidth: '800px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h2>File Matching System</h2>
                            <p style={{ color: '#666', fontSize: '0.9em' }}>
                                <strong>GUID Matching:</strong> Upload Excel file with GUID mappings (GUID → filename → repository match)<br/>
                                <strong>Slug Matching:</strong> Direct URL slug matching for non-GUID links (slug → repository match)<br/>
                                Both methods use fuzzy matching to find SharePoint repository items.
                            </p>
                        </div>
                        <button 
                            onClick={() => setShowGuidMatchModal(false)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            Close
                        </button>
                    </div>

                    {/* File Upload Section */}
                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '15px', 
                        borderRadius: '4px', 
                        marginBottom: '20px',
                        border: '2px dashed #dee2e6'
                    }}>
                        <h4>Step 1: Upload Excel File (Optional for GUID Matching)</h4>
                        <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '10px' }}>
                            Expected format: Column A = Title, Column B = GUID, Column C = Filename<br/>
                            <strong>Note:</strong> Excel file is only needed for GUID-based links. Slug-based links work without Excel.
                        </p>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleExcelFileChange}
                            style={{
                                marginBottom: '10px',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                width: '100%'
                            }}
                        />
                        {excelFile && (
                            <div style={{ 
                                padding: '8px', 
                                backgroundColor: '#d4edda', 
                                border: '1px solid #c3e6cb', 
                                borderRadius: '4px',
                                color: '#155724',
                                fontSize: '0.9em'
                            }}>
                                ✅ File loaded: {excelFile.name}
                            </div>
                        )}
                        
                        {/* Link Statistics */}
                        <div style={{ 
                            marginTop: '15px', 
                            padding: '10px', 
                            backgroundColor: '#e9ecef', 
                            borderRadius: '4px',
                            fontSize: '0.85em'
                        }}>
                            <strong>Available Links:</strong><br/>
                            🔑 GUID-based: {foundLinks.filter(link => isMemberPortalLink(link.url) && extractGuidFromUrl(link.url)).length} links<br/>
                            🏷️ Slug-based: {foundLinks.filter(link => isMemberPortalLink(link.url) && extractSlugFromUrl(link.url) && !extractGuidFromUrl(link.url)).length} links
                        </div>
                    </div>

                    {/* Process Button */}
                    <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                        <button 
                            onClick={handleProcessGuidMatching}
                            disabled={!excelFile || isMatching}
                            style={{
                                padding: '12px 24px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: excelFile && !isMatching ? '#28a745' : '#6c757d',
                                color: 'white',
                                cursor: excelFile && !isMatching ? 'pointer' : 'not-allowed',
                                fontSize: '1.1em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: '0 auto'
                            }}
                        >
                            <FontAwesomeIcon 
                                icon={isMatching ? faSpinner : faRobot} 
                                spin={isMatching}
                            />
                            {isMatching ? 'Processing...' : 'Process GUID Matching'}
                        </button>
                    </div>

                    {/* Results Section */}
                    {matchedResults.length > 0 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <div>
                                    <h3>Matching Results ({showNewsReferencedOnly ? matchedResults.filter(r => r.newsLink).length : matchedResults.length})</h3>
                                    <div style={{ marginTop: '8px' }}>
                                        <label style={{ fontSize: '0.9em', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={showNewsReferencedOnly}
                                                onChange={(e) => setShowNewsReferencedOnly(e.target.checked)}
                                                style={{ marginRight: '4px' }}
                                            />
                                            🎯 Show only files referenced in news articles ({matchedResults.filter(r => r.newsLink).length} items)
                                        </label>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button 
                                        onClick={handleExportMatchResults}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '4px',
                                            border: '1px solid #28a745',
                                            backgroundColor: 'white',
                                            color: '#28a745',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        📊 Export Results
                                    </button>
                                </div>
                            </div>

                            {/* Summary Stats */}
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(4, 1fr)', 
                                gap: '10px', 
                                marginBottom: '20px' 
                            }}>
                                <div style={{ 
                                    backgroundColor: '#d4edda', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    textAlign: 'center' 
                                }}>
                                    <strong>{showNewsReferencedOnly 
                                        ? matchedResults.filter(r => r.status === 'Strong Match' && r.newsLink).length
                                        : matchedResults.filter(r => r.status === 'Strong Match').length}</strong>
                                    <br />Strong Matches
                                </div>
                                <div style={{ 
                                    backgroundColor: '#fff3cd', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    textAlign: 'center' 
                                }}>
                                    <strong>{showNewsReferencedOnly 
                                        ? matchedResults.filter(r => r.status === 'Weak Match' && r.newsLink).length
                                        : matchedResults.filter(r => r.status === 'Weak Match').length}</strong>
                                    <br />Weak Matches
                                </div>
                                <div style={{ 
                                    backgroundColor: '#f8d7da', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    textAlign: 'center' 
                                }}>
                                    <strong>{showNewsReferencedOnly 
                                        ? matchedResults.filter(r => r.status === 'No Match' && r.newsLink).length
                                        : matchedResults.filter(r => r.status === 'No Match').length}</strong>
                                    <br />No Matches
                                </div>
                                <div style={{ 
                                    backgroundColor: '#e2e3e5', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    textAlign: 'center' 
                                }}>
                                    <strong>{matchedResults.filter(r => r.newsLink).length}</strong>
                                    <br />Referenced in News
                                </div>
                            </div>

                            {/* Results Table */}
                            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
                                    <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                                        <tr>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Source Title</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>GUID/Slug</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Article ID</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Article Type</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Repository Match</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Match Score</th>
                                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>In News?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matchedResults
                                            .filter(result => !showNewsReferencedOnly || result.newsLink)
                                            .map((result, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                                                <td style={{ padding: '8px' }}>
                                                    <span style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: result.status === 'Strong Match' ? '#d4edda' :
                                                                       result.status === 'Weak Match' ? '#fff3cd' : '#f8d7da',
                                                        color: result.status === 'Strong Match' ? '#155724' :
                                                               result.status === 'Weak Match' ? '#856404' : '#721c24'
                                                    }}>
                                                        {result.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '8px', maxWidth: '200px', wordBreak: 'break-word' }}>
                                                    {result.guidMapping?.title || result.slugMapping?.title}
                                                </td>
                                                <td style={{ padding: '8px' }}>
                                                    <div>
                                                        {result.guidMapping?.guid && (
                                                            <span style={{ 
                                                                backgroundColor: '#e3f2fd', 
                                                                padding: '2px 6px', 
                                                                borderRadius: '3px', 
                                                                fontSize: '0.8em',
                                                                display: 'block',
                                                                marginBottom: '2px'
                                                            }}>
                                                                🔑 {result.guidMapping.guid}
                                                            </span>
                                                        )}
                                                        {result.slugMapping?.slug && (
                                                            <span style={{ 
                                                                backgroundColor: '#e8f5e8', 
                                                                padding: '2px 6px', 
                                                                borderRadius: '3px', 
                                                                fontSize: '0.8em',
                                                                display: 'block'
                                                            }}>
                                                                🏷️ {result.slugMapping.slug}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    {result.newsLink ? (
                                                        <span style={{ 
                                                            color: '#007bff', 
                                                            fontWeight: 'bold',
                                                            backgroundColor: '#e3f2fd',
                                                            padding: '2px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '0.85em'
                                                        }}>
                                                            {result.newsLink.articleId}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#999', fontSize: '0.8em' }}>-</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    {result.newsLink ? (
                                                        <span style={{
                                                            padding: '2px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '0.8em',
                                                            backgroundColor: result.newsLink.articleType === 'Submitted' ? '#e3f2fd' : 
                                                                           result.newsLink.articleType === 'Weekly' ? '#f3e5f5' : '#e8f5e8',
                                                            color: result.newsLink.articleType === 'Submitted' ? '#1976d2' : 
                                                                   result.newsLink.articleType === 'Weekly' ? '#7b1fa2' : '#388e3c'
                                                        }}>
                                                            {result.newsLink.articleType}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#999', fontSize: '0.8em' }}>-</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', maxWidth: '200px', wordBreak: 'break-word' }}>
                                                    {result.repositoryMatch ? (
                                                        <div>
                                                            <strong>{result.repositoryMatch.title}</strong>
                                                            <br />
                                                            <small style={{ color: '#666' }}>
                                                                {result.repositoryMatch.fileName}
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#999' }}>No match found</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px' }}>
                                                    {result.matchScore > 0 ? (
                                                        <div style={{ 
                                                            width: '60px', 
                                                            height: '10px', 
                                                            backgroundColor: '#e9ecef', 
                                                            borderRadius: '5px',
                                                            position: 'relative'
                                                        }}>
                                                            <div style={{
                                                                width: `${result.matchScore * 100}%`,
                                                                height: '100%',
                                                                backgroundColor: result.matchScore > 0.8 ? '#28a745' : 
                                                                               result.matchScore > 0.5 ? '#ffc107' : '#dc3545',
                                                                borderRadius: '5px'
                                                            }}></div>
                                                            <small style={{ fontSize: '0.7em' }}>
                                                                {(result.matchScore * 100).toFixed(0)}%
                                                            </small>
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ padding: '8px' }}>
                                                    {result.newsLink ? (
                                                        <span style={{ color: '#28a745' }}>✅ Yes</span>
                                                    ) : (
                                                        <span style={{ color: '#dc3545' }}>No</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };



    return (
        <div className='news-management'>
            {isLoading ? (
                <div className="loading-container" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px'
                }}>
                     <LoadingSpinner  />
                </div>
            ) : (
                <>
                    <div className="news-controls" style={{ marginBottom: '20px' }}>
                        <div className="button-group" style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                className="primary-btn" 
                                onClick={handleNewArticle}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon icon={faNewspaper} /> Add News
                            </button>
                            <button 
                                className="secondary-btn" 
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'white',
                                    color: '#6c757d',
                                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={faRotateRight} 
                                    spin={isRefreshing}
                                /> 
                                {isRefreshing ? 'Refreshing...' : 'Refresh'}
                            </button>
                            <button 
                                className="secondary-btn" 
                                onClick={handleIndexNews}
                                disabled={isIndexingNews}
                                title="Index all published news articles into Qdrant for AI search"
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'white',
                                    color: '#6c757d',
                                    cursor: isIndexingNews ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isIndexingNews ? faSpinner : faRobot} 
                                    spin={isIndexingNews}
                                /> 
                                {isIndexingNews ? 'Indexing News...' : 'Index News'}
                            </button>
                            {/* <button 
                                className="secondary-btn" 
                                onClick={handleBulkIndex}
                                disabled={isIndexing}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'white',
                                    color: '#6c757d',
                                    cursor: isIndexing ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isIndexing ? faSpinner : faRobot} 
                                    spin={isIndexing}
                                /> 
                                {isIndexing ? 'Indexing...' : 'Index All'}
                            </button> */}


                        </div>
                    </div>

                    <div className="news-stats">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faNewspaper} />
                            </div>
                            <div className="stat-details">
                                <h3>Total Articles</h3>
                                <p className="stat-number">{newsArticles.length}</p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faEye} />
                            </div>
                            <div className="stat-details">
                                <h3>Published</h3>
                                <p className="stat-number">
                                    {newsArticles.filter(article => article.published).length}
                                </p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">
                                <FontAwesomeIcon icon={faClock} />
                            </div>
                            <div className="stat-details">
                                <h3>Draft</h3>
                                <p className="stat-number">
                                    {newsArticles.filter(article => !article.published).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="content-toolbar">
                        {/* Search Section */}
                        <div className="toolbar-section search-section">
                            <div className="search-box">
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                                <input
                                    type="text"
                                    placeholder="Search news articles..."
                                    value={newsSearchTerm}
                                    onChange={(e) => setNewsSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Filters Section */}
                        <div className="toolbar-section ">
                            <div className="filter-box">
                                <select
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="weekly">Weekly News</option>
                                    <option value="submitted">Submitted Articles</option>
                                </select>
                                <FontAwesomeIcon icon={faFilter} />
                            </div>

                            <div className="filter-box">
                                <select
                                    value={selectedWeeklySection}
                                    onChange={(e) => setSelectedWeeklySection(e.target.value)}
                                >
                                    <option value="all">All Sections</option>
                                    {weeklySections.map(section => (
                                        <option key={section} value={section}>{section}</option>
                                    ))}
                                </select>
                                <FontAwesomeIcon icon={faFilter} />
                            </div>

                            <div className="filter-box">
                                <select
                                    value={showFeaturedOnly ? 'featured' : 'all'}
                                    onChange={(e) => setShowFeaturedOnly(e.target.value === 'featured')}
                                >
                                    <option value="all">All Articles</option>
                                    <option value="featured">Featured Only</option>
                                </select>
                                <FontAwesomeIcon icon={faStar} />
                            </div>
                        </div>
                    </div>

                    <div className="news-table">
                        <table>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('portalPublishDate')} className="sortable">
                                        Portal Publish Date <SortIndicator column="portalPublishDate" />
                                    </th>
                                    <th onClick={() => handleSort('date')} className="sortable">
                                        Issue Date <SortIndicator column="date" />
                                    </th>
                                    <th onClick={() => handleSort('title')} className="sortable">
                                        Title <SortIndicator column="title" />
                                    </th>
                                    <th onClick={() => handleSort('type')} className="sortable">
                                        Type <SortIndicator column="type" />
                                    </th>
                                    <th onClick={() => handleSort('status')} className="sortable">
                                        Status <SortIndicator column="status" />
                                    </th>
                                    <th>Media</th>
                                    <th onClick={() => handleSort('featured')} className="sortable" style={{ width: '50px', textAlign: 'center' }}>
                                        <FontAwesomeIcon 
                                            icon={faStar} 
                                            style={{ fontSize: '0.9em', color: '#6c757d' }} 
                                            title="Featured Articles"
                                        />
                                    </th>
                                    <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentArticles.map(article => (
                                    <tr 
                                        key={getUniqueKey(article)} 
                                        onClick={() => handleArticleClick(article)} 
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{displayDate(article.portalPublishDate)}</td>
                                        <td>{displayDate(article.issueDate)}</td>
                                        <td>{article.title}</td>
                                        <td>
                                            <span className={`type-badge ${getArticleTypeBadge(article).toLowerCase()}`}>
                                                {getArticleTypeBadge(article)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${article.published ? 'published' : 'draft'}`}>
                                                {article.published ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="media-column">
                                            {getMediaIcons(article)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {article.isFeatured && (
                                                <FontAwesomeIcon 
                                                    icon={faStar} 
                                                    style={{ 
                                                        fontSize: '0.9em',
                                                        color: '#FFD700'
                                                    }} 
                                                    title={article.featuredIndefinitely ? 
                                                        'Featured Indefinitely' : 
                                                        `Featured: ${displayDate(article.featuredStartDate)} - ${displayDate(article.featuredEndDate)}`
                                                    }
                                                />
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <FontAwesomeIcon 
                                                icon={faTrash} 
                                                style={{ 
                                                    fontSize: '0.9em',
                                                    color: '#dc3545',
                                                    cursor: 'pointer',
                                                    padding: '4px'
                                                }} 
                                                title={`Delete "${article.title}"`}
                                                onClick={(e) => handleDeleteArticle(article, e)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    Previous
                                </button>
                                <span className="pagination-info">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>

                    {showNewArticleForm && (() => {
                        
                        
                        // Safety check: If no galleries are available, try to load them
                        if (galleries.length === 0 && !isLoadingGalleries) {
                 
                            loadGalleries();
                        }
                        
                        return (
                        <NewArticleForm
                            onClose={handleNewArticleClose}
                            onSave={handleNewArticleSave}
                            galleries={galleries}
                            documentTypes={documentTypes}
                            workingGroups={workingGroups}
                            onUploadDocument={onUploadDocument}
                            onFetchDocuments={fetchDocuments}
                            onRefreshGalleries={refreshGalleries}
                            weeklySections={weeklySections}
                            initialData={selectedArticle}
                            documents={repositoryItems}
                            onRefreshNews={fetchNews}
                        />
                        );
                    })()}
                    <LinksModal />
                    {showArticleForm && selectedArticleForEdit && (() => {
                        
                        return (
                        <NewArticleForm
                            onClose={handleArticleFormClose}
                            onSave={handleArticleFormSave}
                            galleries={galleries}
                            documentTypes={documentTypes}
                            workingGroups={workingGroups}
                            onUploadDocument={onUploadDocument}
                            onFetchDocuments={fetchDocuments}
                            onRefreshGalleries={refreshGalleries}
                            weeklySections={weeklySections}
                            initialData={selectedArticleForEdit}
                            documents={repositoryItems}
                            onRefreshNews={fetchNews}
                        />
                        );
                    })()}
                    <GuidMatchModal />
                </>
            )}
        </div>
    );
}

NewsManagement.propTypes = {
    newsArticles: PropTypes.array.isRequired,
    setNewsArticles: PropTypes.func,
    weeklySections: PropTypes.array.isRequired,
    repositoryItems: PropTypes.array,
    documentTypes: PropTypes.array,
    workingGroups: PropTypes.array,
    onUploadDocument: PropTypes.func.isRequired,
    currentUser: PropTypes.object,
    fetchNews: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default NewsManagement; 